// useTripForm — the EditTripModal form/edit state machine.
//
// Extracted verbatim from `EditTripModal.js` (behavior-preserving refactor,
// issue #240). This hook owns the modal's editable working copy of the trip and
// every meeting/event mutation handler and UI-toggle flag EXCEPT the save flow
// (see `useSaveTrip`):
//
//   State:
//     - editedTrip        (working copy, seeded via initializeEditedTrip(trip))
//     - showAddMeeting    (Add-Meeting sub-view toggle)
//     - editingMeeting    (Edit-Meeting sub-view target, null when closed)
//     - showPDFUpdater    (PDF-updater sub-view toggle)
//     - showAddEvent      (Add-Event sub-view toggle)
//     - newEventType      (the manual event type being added, '' when none)
//
// Handlers (call order, params, and side effects preserved EXACTLY):
//     - handleAddMeeting   — appends a meeting (addMeetingToTrip) then closes
//                            the Add-Meeting sub-view. `async`, matching source.
//     - handleEditMeeting  — replaces a meeting (editMeetingInTrip) then clears
//                            the editing target. `async`, matching source.
//     - handleDeleteMeeting— resolves the meeting (findMeetingById), gates on the
//                            EXACT window.confirm prompt, then removes it
//                            (deleteMeetingFromTrip). No-op if not found/declined.
//     - clearAllMeetings   — gates on the EXACT window.confirm prompt (using the
//                            live meeting count), then empties meetings
//                            (clearAllMeetingsFromTrip).
//     - handleManualAddEvent(eventType) — sets the new event type and opens the
//                            Add-Event sub-view (the toolbar trigger).
//     - handleAddManualEvent(eventData) — the Add-Event submit: looks up the
//                            type config (getEventTypeConfig); on an unknown type
//                            logs `console.error('Unknown event type:', ...)`,
//                            resets the form, and EARLY-RETURNS — exactly as the
//                            source; otherwise generates the id (generateEventId),
//                            builds the event (buildManualEvent), appends it
//                            (addEventToTrip), then resets the form.
//     - handlePDFUpload    — replaces the whole trip with the PDF-updated trip
//                            and closes the PDF-updater sub-view.
//
// All edited-trip mutations are issued through `setEditedTrip(prev => ...)` so
// the 10.2 logic builders (initializeEditedTrip, addMeetingToTrip,
// editMeetingInTrip, findMeetingById, deleteMeetingFromTrip,
// clearAllMeetingsFromTrip, getEventTypeConfig, generateEventId, buildManualEvent,
// addEventToTrip) are reused rather than duplicated. Derived header values
// (getTripCounts/formatTripDateRange over the original `trip`, getMeetingsSummary
// over `editedTrip`) are exposed for the container to render the same UI.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useCallback } from 'react';

import initializeEditedTrip from '../logic/initializeEditedTrip';
import addMeetingToTrip from '../logic/addMeetingToTrip';
import editMeetingInTrip from '../logic/editMeetingInTrip';
import findMeetingById from '../logic/findMeetingById';
import deleteMeetingFromTrip from '../logic/deleteMeetingFromTrip';
import clearAllMeetingsFromTrip from '../logic/clearAllMeetingsFromTrip';
import { getEventTypeConfig } from '../logic/eventTypeConfig';
import generateEventId from '../logic/generateEventId';
import buildManualEvent from '../logic/buildManualEvent';
import addEventToTrip from '../logic/addEventToTrip';
import getTripCounts from '../logic/getTripCounts';
import formatTripDateRange from '../logic/formatTripDateRange';
import getMeetingsSummary from '../logic/getMeetingsSummary';

/**
 * Owns the EditTripModal form/edit state and all meeting/event mutations.
 *
 * @param {Object} params
 * @param {Object} params.trip - The trip passed to the modal. Seeds `editedTrip`
 *   via `initializeEditedTrip` and backs the read-only header counts/date range.
 * @returns {{
 *   editedTrip: Object,
 *   setEditedTrip: import('react').Dispatch<import('react').SetStateAction<Object>>,
 *   showAddMeeting: boolean,
 *   setShowAddMeeting: (value: boolean) => void,
 *   editingMeeting: (Object|null),
 *   setEditingMeeting: (value: (Object|null)) => void,
 *   showPDFUpdater: boolean,
 *   setShowPDFUpdater: (value: boolean) => void,
 *   showAddEvent: boolean,
 *   setShowAddEvent: (value: boolean) => void,
 *   newEventType: string,
 *   setNewEventType: (value: string) => void,
 *   handleAddMeeting: (meeting: Object) => Promise<void>,
 *   handleEditMeeting: (updatedMeeting: Object) => Promise<void>,
 *   handleDeleteMeeting: (meetingId: *) => void,
 *   clearAllMeetings: () => void,
 *   handleManualAddEvent: (eventType: string) => void,
 *   handleAddManualEvent: (eventData: Object) => void,
 *   handlePDFUpload: (updatedTrip: Object) => void,
 *   tripCounts: { flights: number, hotels: number, carRentals: number },
 *   tripDateRange: string,
 *   meetingsSummary: { count: number, showBadge: boolean, showClearAll: boolean, isEmpty: boolean }
 * }} The form API consumed by the container.
 */
export function useTripForm({ trip }) {
  const [editedTrip, setEditedTrip] = useState(() => initializeEditedTrip(trip));
  const [showAddMeeting, setShowAddMeeting] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [showPDFUpdater, setShowPDFUpdater] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEventType, setNewEventType] = useState('');

  // ── Add a meeting, then close the Add-Meeting sub-view ──────────────────────
  const handleAddMeeting = useCallback(async (meeting) => {
    setEditedTrip(prev => addMeetingToTrip(prev, meeting));
    setShowAddMeeting(false);
  }, []);

  // ── Replace a meeting, then clear the editing target ────────────────────────
  const handleEditMeeting = useCallback(async (updatedMeeting) => {
    setEditedTrip(prev => editMeetingInTrip(prev, updatedMeeting));
    setEditingMeeting(null);
  }, []);

  // ── Delete a meeting behind the confirmation gate (exact prompt) ────────────
  const handleDeleteMeeting = useCallback((meetingId) => {
    const meeting = findMeetingById(editedTrip, meetingId);
    if (meeting && window.confirm(`Are you sure you want to delete the meeting "${meeting.meetingTitle}" with ${meeting.companyName}?`)) {
      setEditedTrip(prev => deleteMeetingFromTrip(prev, meetingId));
    }
  }, [editedTrip]);

  // ── Clear all meetings behind the confirmation gate (exact prompt) ──────────
  const clearAllMeetings = useCallback(() => {
    if (window.confirm(`Are you sure you want to delete all ${editedTrip.meetings.length} business meetings?`)) {
      setEditedTrip(prev => clearAllMeetingsFromTrip(prev));
    }
  }, [editedTrip]);

  // ── Toolbar trigger: choose a type and open the Add-Event sub-view ──────────
  const handleManualAddEvent = useCallback((eventType) => {
    setNewEventType(eventType);
    setShowAddEvent(true);
  }, []);

  // ── Add-Event submit: build + append the event, or reset on unknown type ────
  const handleAddManualEvent = useCallback((eventData) => {
    const config = getEventTypeConfig(newEventType);
    if (!config) {
      console.error('Unknown event type:', newEventType);
      setShowAddEvent(false);
      setNewEventType('');
      return;
    }

    const newEvent = buildManualEvent(config, eventData, generateEventId(config.idPrefix));

    setEditedTrip(prev => addEventToTrip(prev, config, newEvent));

    setShowAddEvent(false);
    setNewEventType('');
  }, [newEventType]);

  // ── Replace the trip from a PDF update, then close the PDF sub-view ─────────
  const handlePDFUpload = useCallback((updatedTrip) => {
    setEditedTrip(updatedTrip);
    setShowPDFUpdater(false);
  }, []);

  // Derived, read-only header values (counts/date range read the original `trip`,
  // meetings summary reads the live `editedTrip`), matching the source exactly.
  const tripCounts = getTripCounts(trip);
  const tripDateRange = formatTripDateRange(trip);
  const meetingsSummary = getMeetingsSummary(editedTrip);

  return {
    editedTrip,
    setEditedTrip,
    showAddMeeting,
    setShowAddMeeting,
    editingMeeting,
    setEditingMeeting,
    showPDFUpdater,
    setShowPDFUpdater,
    showAddEvent,
    setShowAddEvent,
    newEventType,
    setNewEventType,
    handleAddMeeting,
    handleEditMeeting,
    handleDeleteMeeting,
    clearAllMeetings,
    handleManualAddEvent,
    handleAddManualEvent,
    handlePDFUpload,
    tripCounts,
    tripDateRange,
    meetingsSummary,
  };
}

export default useTripForm;
